// components/leaders/LeaderProfileInfo.tsx
import { Card } from "@/components/ui/card";
import { LeaderData } from "@/utils/leadershipInfo";
import { Mail, Phone } from "lucide-react";
import GcsImage from "@/components/common/GcsImage";
import { usePathname } from "next/navigation";
import { FiLinkedin } from "react-icons/fi";
import { RINGBA_DNI_PHONE, formatPhoneNumber } from "@/lib/constants";

interface ProfileInfoProps {
  leader: LeaderData;
}

export default function ProfileInfo({ leader }: ProfileInfoProps) {
  const pathname = usePathname();
  return (
    <div className="bg-[#F5F3FF] py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <div className="max-w-[65%] mx-auto relative">
              <GcsImage
                path={leader.image}
                alt={leader.name}
                className="w-full h-auto object-cover rounded-2xl"
              />
            </div>
          </div>

          <div className="lg:col-span-8 space-y-8">
            <div>
              <h2 className="text-3xl font-bold mb-4">About</h2>
              <p className="text-lg leading-relaxed whitespace-pre-line">{leader.bio}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {leader?.education && (
                <Card className="p-6 bg-white border-border">
                  <h3 className="text-xl font-bold mb-3">Education</h3>
                  <p>{leader.education}</p>
                </Card>
              )}

              {leader?.expertise && (
                <Card className="p-6 bg-white border-border">
                  <h3 className="text-xl font-bold mb-3">Expertise</h3>
                  <p>{leader.expertise}</p>
                </Card>
              )}

              {leader?.credentials && (
                <Card className="p-6 bg-white border-border">
                  <h3 className="text-xl font-bold mb-3">Credentials</h3>
                  <p>{leader.credentials}</p>
                </Card>
              )}

              {leader.passion && (
                <Card className="p-6 bg-white border-border">
                  <h3 className="text-xl font-bold mb-3">Passion</h3>
                  <p>{leader.passion}</p>
                </Card>
              )}
            </div>

            <div className="flex flex-wrap gap-6 pt-4 sm:">
              {leader.email && (
                <a
                  href={`mailto:${leader.email}`}
                  className="flex items-center gap-2 text-lg text-muted-foreground hover:text-primary"
                >
                  <Mail className="w-6 h-6" />
                  Email
                </a>
              )}

              {leader.linkedin && (
                <a
                  href={leader.linkedin}
                  target="_blank"
                  className="flex items-center gap-2 text-lg text-muted-foreground hover:text-primary"
                >
                  <FiLinkedin className="w-6 h-6" />
                  LinkedIn
                </a>
              )}
              {pathname.includes("careers") && (
                <a
                  href={`tel:${RINGBA_DNI_PHONE}`}
                  className="flex items-center justify-start md:justify-start gap-2 text-lg text-muted-foreground hover:text-primary transition-colors w-full md:w-auto"
                  suppressHydrationWarning={true} // Ringba's Number Insertion changes the phone number client-side
                >
                  <Phone className="w-6 h-6" />
                  <span>{formatPhoneNumber(RINGBA_DNI_PHONE)}</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
